import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, chapterLists, mangas } from '@/mocks/manga';
import { SITE_URL } from '@/utils/site';
import ChapterList from './components/ChapterList';
import Comments from './components/Comments';
import RelatedMangas from './components/RelatedMangas';

export default function MangaDetailPage() {
  const { id } = useParams();
  const manga = mangas.find((m) => m.id === id);
  const [favorited, setFavorited] = useState(false);

  if (!manga) {
    return <Navigate to="/category" replace />;
  }

  const chapters = chapterLists.find((c) => c.mangaId === manga.id)?.chapters ?? [];
  const firstChapter = chapters[0];
  const category = categories.find((c) => manga.categoryIds.includes(c.id));

  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ComicSeries',
    name: `${manga.title}漫画`,
    url: `${SITE_URL}/manga/${manga.id}`,
    image: manga.cover,
    description: manga.description,
    author: { '@type': 'Organization', name: manga.author },
    inLanguage: 'zh-CN',
    numberOfIssues: manga.totalChapters,
    dateModified: new Date().toISOString().split('T')[0],
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pb-16 pt-24 md:px-6 md:pt-28">
      <Seo
        title={`${manga.title}漫画全集在线免费阅读_073漫画网`}
        description={`${manga.description}点击进入073漫画网即可免费下拉式阅读《${manga.title}》最新章节，漫画免费阅读、每日更新。`}
        keywords={`${manga.title}漫画,漫画免费阅读,下拉式漫画,073漫画网,${manga.tags[0]}漫画`}
        canonicalPath={`/manga/${manga.id}`}
        type="article"
        image={manga.cover}
        jsonLd={[seoJsonLd]}
      />

      <Breadcrumb
        items={[
          { label: '漫画分类', to: '/category' },
          ...(category ? [{ label: category.name, to: `/category/${category.slug}` }] : []),
          { label: manga.title },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
        <div className="mx-auto w-full max-w-[260px] lg:max-w-none">
          <div className="relative overflow-hidden rounded-lg bg-background-100">
            <div className="aspect-[2/3] w-full">
              <img
                src={manga.cover}
                alt={`${manga.title}漫画封面`}
                title={`${manga.title}漫画免费阅读`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <span className="absolute left-3 top-3 whitespace-nowrap rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
              {manga.status}
            </span>
          </div>

          <div className="mt-4 flex flex-col gap-2">
            {firstChapter && (
              <Link
                to={`/read/${manga.id}/${firstChapter.id}`}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-fill" aria-hidden="true" />
                开始阅读
              </Link>
            )}
            <button
              type="button"
              onClick={() => setFavorited((v) => !v)}
              className={`flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 text-sm transition-colors ${
                favorited
                  ? 'bg-secondary-500 text-background-50'
                  : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
              }`}
            >
              <i className={favorited ? 'ri-star-fill' : 'ri-star-line'} aria-hidden="true" />
              {favorited ? '已收藏' : '收藏作品'}
            </button>
          </div>
        </div>

        <div className="min-w-0">
          <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">
            《{manga.title}》漫画
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
              <i className="ri-user-3-line" aria-hidden="true" />
              {manga.author}
            </span>
            {manga.tags.map((tag) => (
              <Link
                key={tag}
                to="/category"
                className="whitespace-nowrap rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
              >
                #{tag}
              </Link>
            ))}
          </div>

          <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: '总阅读', value: `${(manga.views / 10000).toFixed(0)}万`, icon: 'ri-eye-line' },
              { label: '好评', value: `${manga.rating}分`, icon: 'ri-star-fill' },
              { label: '人气', value: `${manga.likes / 1000}万赞`, icon: 'ri-thumb-up-line' },
              { label: '章节', value: `${manga.totalChapters}话`, icon: 'ri-book-3-line' },
            ].map((item) => (
              <div key={item.label} className="rounded-lg bg-background-100 px-3 py-3">
                <dt className="flex items-center gap-1.5 text-xs text-foreground-600">
                  <i className={`${item.icon} text-primary-600`} aria-hidden="true" />
                  {item.label}
                </dt>
                <dd className="mt-1 font-heading text-lg text-foreground-950">{item.value}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-6">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
              <i className="ri-file-text-line text-primary-600" aria-hidden="true" />
              漫画简介
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">{manga.description}</p>
            <p className="mt-3 text-xs leading-relaxed text-foreground-600">
              最新更新：{manga.latestChapter}（{manga.updateTime}）。《{manga.title}》漫画在073漫画网支持免费下拉式阅读，无需下载即可在线畅读。
            </p>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 text-sm text-foreground-700">
              <i className="ri-shield-check-line text-accent-600" aria-hidden="true" />
              正版授权 · 免费阅读
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-foreground-300 sm:block" />
            <span className="flex items-center gap-1 text-sm text-foreground-700">
              <i className="ri-smartphone-line text-accent-600" aria-hidden="true" />
              手机电脑均可阅读
            </span>
            <span className="hidden h-1 w-1 rounded-full bg-foreground-300 sm:block" />
            <span className="flex items-center gap-1 text-sm text-foreground-700">
              <i className="ri-refresh-line text-accent-600" aria-hidden="true" />
              每日稳定更新
            </span>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        <ChapterList manga={manga} chapters={chapters} />
        <Comments mangaId={manga.id} />
      </div>

      <RelatedMangas manga={manga} />
    </div>
  );
}